/**
 * Subscription plan — business-service is the authority (`myPlan`, `planCatalog`),
 * ai-service reports the assistant's monthly spend vs the plan budget.
 */
import i18n from "../../i18n";

const BUSINESS_URL = process.env.REACT_APP_BUSINESS_SERVICE_BASE_URL || '';
const AI_URL = process.env.REACT_APP_AI_SERVICE_BASE_URL || '';

async function gqlPost<T>(url: string, token: string, query: string, variables?: object): Promise<T> {
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'Accept-Language': i18n.language || 'pt',
        },
        body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors) {
        throw new Error(json.errors.map((e: { message: string }) => e.message).join(', '));
    }
    return json.data as T;
}

export type PlanKey = 'AUTONOMO' | 'PRO';

/** Gateable features (business-service PlanCatalog.PlanFeature). */
export type PlanFeatureKey =
    | 'TEAM' | 'COMMISSIONS' | 'WAITLIST' | 'CHAT'
    | 'FINANCE' | 'REPORTS' | 'ROLES' | 'CONVENIOS' | 'EVENTS' | 'AI'
    | 'WAITING_ROOM';

export interface MyPlan {
    plan: PlanKey;
    status: string;
    founder: boolean;
    currentPeriodEnd: string | null;
    enforcing: boolean;
    /** Professionals allowed. 0 = unlimited (founders). */
    teamCap: number;
    teamMemberCount: number;
    /** Paid seats beyond the one included in the base price. */
    extraProfessionals: number;
    aiAddon: boolean;
    aiBudgetBrl: number;
    /** base + seats + add-on. 0 for founders. */
    monthlyPriceBrl: number;
    extraProfessionalBrl: number;
    aiAddonBrl: number;
    /** Only a business admin may open the Plano tab / change the plan. Non-admins
     *  also get the billing fields above zeroed by the backend. */
    canManagePlan: boolean;
    features: PlanFeatureKey[];
}

export interface PlanView {
    key: PlanKey;
    priceBrl: number;
    includedProfessionals: number;
    extraProfessionalBrl: number;
    aiAddonBrl: number;
    aiAddonBudgetBrl: number;
    paid: boolean;
    features: PlanFeatureKey[];
}

export interface AiMonthlyUsage {
    month: string;
    spentBrl: number;
    budgetBrl: number;
    included: boolean;
    blocked: boolean;
}

const MY_PLAN_FIELDS = `plan status founder currentPeriodEnd enforcing teamCap teamMemberCount extraProfessionals aiAddon aiBudgetBrl monthlyPriceBrl extraProfessionalBrl aiAddonBrl canManagePlan features`;

export async function getMyPlan(businessId: number | string, token: string): Promise<MyPlan> {
    const data = await gqlPost<{ myPlan: MyPlan }>(
        BUSINESS_URL, token,
        `query myPlan($businessId: ID!) { myPlan(businessId: $businessId) { ${MY_PLAN_FIELDS} } }`,
        { businessId: Number(businessId) },
    );
    return data.myPlan;
}

export async function getPlanCatalog(token: string): Promise<PlanView[]> {
    const data = await gqlPost<{ planCatalog: PlanView[] }>(
        BUSINESS_URL, token,
        `query { planCatalog { key priceBrl includedProfessionals extraProfessionalBrl aiAddonBrl aiAddonBudgetBrl paid features } }`,
    );
    return data.planCatalog;
}

export async function getAiMonthlyUsage(businessId: number | string, token: string): Promise<AiMonthlyUsage> {
    const data = await gqlPost<{ aiMonthlyUsage: AiMonthlyUsage }>(
        AI_URL, token,
        `query aiMonthlyUsage($businessId: ID!) { aiMonthlyUsage(businessId: $businessId) { month spentBrl budgetBrl included blocked } }`,
        { businessId: Number(businessId) },
    );
    return data.aiMonthlyUsage;
}

/** The plan that unlocks a feature. With one paid plan the answer is always PRO;
 *  AI additionally needs the add-on (the upsell copy says so). */
export function requiredPlanFor(feature: PlanFeatureKey, catalog: PlanView[] | null): PlanKey {
    if (catalog) {
        const paid = catalog.find((c) => c.features.includes(feature));
        if (paid) return paid.key;
    }
    return 'PRO';
}

/** Monthly total for a given number of professionals on a paid plan. */
export function monthlyPriceFor(plan: PlanView, professionals: number, withAi = false): number {
    const extras = Math.max(0, professionals - plan.includedProfessionals);
    return plan.priceBrl + extras * plan.extraProfessionalBrl + (withAi ? plan.aiAddonBrl : 0);
}
